import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';

import { FLAG_CODES, type FlagCode } from '../../../../generated/flags';
import { UNKNOWN_FLAG, flagName, flagSearchTerms, flagSvgPath } from '../../../../services/Flags';
import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { scrollbarThin } from '../../../theme/scrollbar.css';
import { tokens } from '../../../theme/tokens.css';
import { GridPicker, type Searchable, gridPickerStyles } from '../../grid-picker';

interface FlagEntry extends Searchable {
  readonly code: FlagCode;
  readonly name: string;
}

/** Every flag but the unknown placeholder, with the search text of names and aliases built once. */
const FLAG_ENTRIES: readonly FlagEntry[] = FLAG_CODES.filter((code) => code !== UNKNOWN_FLAG).map((code) => {
  const name = flagName(code);
  return {
    code,
    name,
    searchText: flagSearchTerms(code).join(' ').toLowerCase(),
    names: [code, name.toLowerCase()],
  };
});

/**
 * Flag Picker - the menu drawer's Country Flag page
 *
 * The menu's Country Flag row opens it, and so does /flag typed without a
 * name (uiState.menuPage). A search box over a grid of the 275 flags, all but
 * the unknown placeholder, with yours ringed, and a back button to the menu. A
 * pick sends /flag and goes back to the menu, whose Country Flag row shows the
 * new flag once the server confirms it; a flag the server lacks shows there as
 * chosen while the plane flies the closest one the server has.
 *
 * The drawer passes in what it reads from uiState rather than this binding
 * Valtio itself, so the 275 items render again only when the search, the
 * highlight or your flag changes, not on every game state update.
 *
 * @element flag-picker
 */
@customElement('flag-picker')
export class FlagPicker extends LitElement {
  static override styles = [
    tokens,
    gridPickerStyles,
    scrollbarThin,
    css`
      :host {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        /* Declared here rather than inherited from the body: the page owns
           its type, and a slow tap on a flag shouldn't start a text
           selection, callout or grey tap flash. */
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
        --picker-item-width: 90px;
        --picker-item-height: 80px;
      }

      /* Level with the menu button's ✕, which floats over the drawer's top
         right (menu-toolbar: a 31px strip 12px down, a 44px chip 14px down on
         touch). The right padding keeps the title clear of it. */
      .header {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: 6px;
        height: 31px;
        padding: calc(12px + env(safe-area-inset-top, 0px)) 64px 12px 16px;
      }

      .back-btn {
        flex-shrink: 0;
        width: 32px;
        height: 31px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        background: transparent;
        border: 0;
        border-radius: var(--radius-md);
        cursor: pointer;
        opacity: 0.8;
        touch-action: manipulation;
      }

      /* Gated so iOS sticky-hover doesn't leave a tapped button lit. */
      @media (hover: hover) {
        .back-btn:hover {
          opacity: 1;
          background-color: var(--color-overlay-subtle);
        }
      }

      .back-btn:active {
        opacity: 1;
        background-color: var(--color-border-subtle);
      }

      .back-icon {
        width: 22px;
        height: 22px;
        background-color: var(--color-text-white);
        mask: url('/vanilla/ui/menu/chevron-left.svg') center / contain no-repeat;
        -webkit-mask: url('/vanilla/ui/menu/chevron-left.svg') center / contain no-repeat;
      }

      @media (hover: none) {
        .header {
          height: 44px;
          padding-top: calc(14px + env(safe-area-inset-top, 0px));
        }

        .back-btn {
          width: 44px;
          height: 44px;
        }
      }

      .title {
        font-size: var(--font-size-16);
        font-weight: bold;
        color: var(--color-text-white);
        white-space: nowrap;
      }

      .current-flag {
        width: 24px;
        height: 18px;
        object-fit: contain;
        margin-left: 4px;
      }

      .search-box {
        flex-shrink: 0;
        margin: 0 24px 8px;
      }

      .hint {
        flex-shrink: 0;
        margin: 0 24px 12px;
        font-size: var(--font-size-xs);
        color: var(--color-text-tertiary);
      }

      .hint strong {
        color: var(--color-text-secondary);
      }

      /* The grid scrolls under the header and search box. Its bottom inset
         keeps the last row clear of the iOS home indicator. */
      .grid {
        padding: 0 24px calc(12px + env(safe-area-inset-bottom, 0px));
        overscroll-behavior: contain;
      }

      .item img {
        width: 48px;
        height: 36px;
        object-fit: contain;
      }

      .item .label {
        font-size: var(--font-size-xs);
        margin-top: 3px;
        max-width: 100%;
      }

      .item .code {
        font-size: var(--font-size-xxs);
        color: var(--color-text-secondary);
      }

      /* Yours, ringed in the menu's selected green. */
      .item.current {
        box-shadow: inset 0 0 0 2px var(--color-btn-play);
      }

      .item.current .label {
        color: var(--color-text-white);
      }
    `,
  ];

  /** The player's flag, ringed in the grid and shown by the title. */
  @property({ attribute: false }) current: FlagCode | null = null;

  /** Focus the search box on open: true with a keyboard. On a phone or
   *  tablet, focus would throw the on-screen keyboard up over the grid, so
   *  the box waits for a tap there. */
  @property({ attribute: false }) focusSearch = false;

  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  @query('.grid') private grid?: HTMLElement;

  @query('input') private searchInput?: HTMLInputElement;

  private readonly picker = new GridPicker(this, {
    items: FLAG_ENTRIES,
    onPick: (flag) => this.selectFlag(flag.code),
    onClose: () => this.gameApi.showMenuPage('main'),
    grid: () => this.grid,
  });

  override firstUpdated() {
    // After the drawer has had a frame to show; preventScroll so focusing
    // doesn't nudge the sliding drawer.
    if (this.focusSearch) {
      requestAnimationFrame(() => this.searchInput?.focus({ preventScroll: true }));
    }
  }

  private readonly handleBack = (e: Event) => {
    // Keep the click from the drawer's backdrop and the game
    e.stopPropagation();
    this.gameApi.playClick();
    this.gameApi.showMenuPage('main');
  };

  /** A clicked or tapped flag. Enter picks one without the click sound. */
  private readonly handleItemClick = (e: Event) => {
    e.stopPropagation();
    const flag = this.picker.itemFrom(e.currentTarget);
    if (flag === undefined) return;
    this.gameApi.playClick();
    this.selectFlag(flag.code);
  };

  private selectFlag(code: FlagCode) {
    this.gameApi.submitChatMessage('/flag ' + code);
    this.gameApi.showMenuPage('main');
  }

  override render() {
    const results = this.picker.results;

    return html`
      <div class="header">
        <button class="back-btn" aria-label="Back to the menu" @click=${this.handleBack}>
          <span class="back-icon"></span>
        </button>
        <span class="title">Country Flag</span>
        ${this.current ? html`<img class="current-flag" src=${flagSvgPath(this.current)} alt="" />` : nothing}
      </div>
      <div class="picker">
        <div class="search-box">
          <input
            type="text"
            placeholder="Search flags..."
            .value=${this.picker.term}
            @input=${this.picker.handleInput}
            @keydown=${this.picker.handleSearchKeyDown}
          />
        </div>
        <div class="hint">
          In chat, <strong>/flag us</strong> sets yours and <strong>-flagus-</strong> shows one inline.
        </div>
        ${
          results.length > 0
            ? html`
                <div class="grid scrollbar-thin">
                  ${results.map(
                    (flag, i) => html`
                      <div
                        class="item ${i === this.picker.selected ? 'selected' : ''} ${
                          flag.code === this.current ? 'current' : ''
                        }"
                        tabindex="0"
                        data-index=${i}
                        @click=${this.handleItemClick}
                        @keydown=${this.picker.handleItemKeyDown}
                        title="${flag.name} (${flag.code})"
                      >
                        <img src="${flagSvgPath(flag.code)}" alt="${flag.code}" loading="lazy" />
                        <div class="label">${flag.name}</div>
                        <div class="code">${flag.code}</div>
                      </div>
                    `,
                  )}
                </div>
              `
            : html`<div class="no-results">No flags matching "${this.picker.term}"</div>`
        }
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'flag-picker': FlagPicker;
  }
}
